import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const tierMeta = {
  single: { badge: "Starter", color: "#A1A1AA" },
  "5pack": { badge: "Popular", color: "var(--cl-orange)" },
  "20case": { badge: "Wholesale", color: "var(--cl-gold)" },
};

export default function MysteryPacks() {
  const [packs, setPacks] = useState([]);
  const [labels, setLabels] = useState([]);
  const [subs, setSubs] = useState([]);
  const [activeLabel, setActiveLabel] = useState("all");
  const [activeSub, setActiveSub] = useState("all");
  const [settings, setSettings] = useState({});
  const { add } = useCart();

  useEffect(() => {
    Promise.all([
      api.get("/products?category=mystery_pack"),
      api.get("/filters?kind=pack_label"),
      api.get("/filters?kind=pack_sub"),
    ]).then(([p, l, s]) => {
      setPacks(p.data);
      setLabels(l.data);
      setSubs(s.data);
    }).catch((err) => console.warn("Failed to load:", err?.message));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const filteredPacks = packs.filter((p) => {
    if (activeLabel !== "all" && p.pack_label !== activeLabel) return false;
    if (activeSub !== "all" && p.pack_sub !== activeSub) return false;
    return true;
  });
  const filtersActive = activeLabel !== "all" || activeSub !== "all";
  const clearFilters = () => { setActiveLabel("all"); setActiveSub("all"); };

  const currentSubs = activeLabel === "all" ? [] : subs.filter((s) => s.parent_key === activeLabel);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
      <div className="max-w-3xl">
        <div className="text-label text-zinc-500">{settings.mystery_kicker || "Wholesale · Bulk Orders"}</div>
        <h1 className="font-display text-5xl sm:text-7xl mt-2">{settings.mystery_title || "Mystery Packs"}</h1>
        <p className="text-zinc-300 text-lg mt-4">
          {settings.mystery_subtitle || "From a single rip to a full 20-pack case — Card Lobster mystery packs are curated for value with transparent odds and a guaranteed floor. Perfect for collectors, hobby shops, and resellers."}
        </p>
      </div>

      {/* Label filter row */}
      {labels.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-2">
          <button
            onClick={() => { setActiveLabel("all"); setActiveSub("all"); }}
            data-testid="pack-label-all"
            className={`text-label px-4 py-2 rounded border transition-colors ${activeLabel === "all" ? "bg-white text-black border-white" : "border-white/10 text-zinc-300 hover:border-[var(--cl-orange)]"}`}
          >
            All Labels
          </button>
          {labels.map((l) => {
            const isActive = activeLabel === l.key;
            const color = l.color || "#FFD700";
            // Contrast fix: for very dark label colors (like BLACK LABEL #111), use white for inactive state
            const isDark = /^#[0-2][0-2][0-2]/i.test(color) || color.toLowerCase() === "#000000";
            const inactiveFg = isDark ? "#FFFFFF" : color;
            const inactiveBorder = isDark ? "rgba(255,255,255,0.35)" : color + "55";
            return (
              <button
                key={l.key}
                onClick={() => { setActiveLabel(l.key); setActiveSub("all"); }}
                data-testid={`pack-label-${l.key}`}
                className="text-label px-4 py-2 rounded border transition-colors"
                style={isActive
                  ? { backgroundColor: color, color: isDark ? "#FFFFFF" : "#000000", borderColor: color }
                  : { borderColor: inactiveBorder, color: inactiveFg }}
              >
                {l.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Sub-category filter row (only when a label is selected) */}
      {currentSubs.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 pl-4 border-l-2 border-white/10">
          <button
            onClick={() => setActiveSub("all")}
            data-testid="pack-sub-all"
            className={`text-xs uppercase tracking-widest px-3 py-1 rounded border ${activeSub === "all" ? "bg-white/10 border-white text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}
          >
            All
          </button>
          {currentSubs.map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveSub(s.key)}
              data-testid={`pack-sub-${s.key}`}
              className={`text-xs uppercase tracking-widest px-3 py-1 rounded border transition-colors ${activeSub === s.key ? "bg-white/10 border-white text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 mt-10" data-testid="packs-grid">
        <div className="col-span-full flex items-center justify-between -mb-2">
          <div className="text-sm text-zinc-400" data-testid="packs-count">
            {filtersActive
              ? <>Showing <span className="text-white font-medium">{filteredPacks.length}</span> of <span className="text-white font-medium">{packs.length}</span> packs</>
              : <>Showing all <span className="text-white font-medium">{packs.length}</span> pack{packs.length === 1 ? "" : "s"}</>
            }
          </div>
          {filtersActive && (
            <button onClick={clearFilters} data-testid="packs-clear-filters"
                    className="text-xs uppercase tracking-widest text-[var(--cl-orange)] hover:underline">
              Clear filters ×
            </button>
          )}
        </div>
        {filteredPacks.length === 0 && (
          <div className="col-span-full text-zinc-500 py-16 text-center">
            <p className="mb-4">No packs match those filters.</p>
            {filtersActive && <button onClick={clearFilters} className="btn-outline" data-testid="packs-empty-clear-filters">Clear filters</button>}
          </div>
        )}
        {filteredPacks.map((p) => {
          const meta = tierMeta[p.pack_tier] || tierMeta.single;
          const label = labels.find((l) => l.key === p.pack_label);
          return (
            <div key={p.id} data-testid={`pack-card-${p.id}`} className="relative border border-white/10 rounded-md bg-[var(--cl-surface)] p-8 flex flex-col card-hover">
              {label && (
                <span className="absolute -top-3 right-6 text-[10px] font-bold tracking-widest px-3 py-1 rounded"
                      style={{ backgroundColor: label.color || "#FFD700", color: "#0A0A0A" }}>
                  {label.label}
                </span>
              )}
              <span className="absolute -top-3 left-6 text-[10px] font-bold tracking-widest px-3 py-1 rounded"
                    style={{ backgroundColor: meta.color, color: "#0A0A0A" }}>
                {meta.badge}
              </span>
              <div className="aspect-square rounded overflow-hidden bg-black mb-6">
                <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
              </div>
              <h3 className="font-display text-2xl">{p.title}</h3>
              <p className="text-sm text-zinc-400 mt-2 min-h-[3.5rem]">{p.description}</p>

              <div className="mt-4 space-y-2 text-sm">
                {p.floor_value != null && <StatRow label="Floor Value" value={`$${p.floor_value.toLocaleString()}`} />}
                {p.ceiling_value != null && <StatRow label="Ceiling Chase" value={`$${p.ceiling_value.toLocaleString()}`} />}
                {p.odds && Object.entries(p.odds).map(([k, v]) => (
                  <StatRow key={k} label={k.replace("_", " ")} value={v} />
                ))}
              </div>

              <div className="mt-6 flex items-end justify-between">
                <span className="font-display text-4xl">${p.price.toLocaleString()}</span>
              </div>
              <button
                data-testid={`pack-buy-${p.id}`}
                onClick={() => { add(p); toast.success("Added to cart — head to cart to check out"); }}
                className="btn-primary mt-4"
              >
                Add to Cart
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex justify-between border-b border-white/5 pb-1">
      <span className="text-label text-zinc-500">{label}</span>
      <span className="text-white font-medium">{value}</span>
    </div>
  );
}
